import { create } from 'zustand';
import { SpotlightMode, PageSearchResult } from './spotlight.types';

const HISTORY_STORAGE_KEY = 'spotlight-history';
const MAX_HISTORY_ITEMS = 10;

interface SpotlightState {
  // UI State
  isOpen: boolean;
  query: string;
  selectedIndex: number;
  mode: SpotlightMode;

  // Animation states
  showActionsButton: boolean;
  showHistoryButton: boolean;
  showPageSearchButton: boolean;

  // Page search results
  pageSearchResults: PageSearchResult[];

  // History
  historyRoutes: string[];

  // Actions
  open: () => void;
  close: () => void;
  setQuery: (query: string) => void;
  setSelectedIndex: (index: number) => void;
  setMode: (mode: SpotlightMode) => void;
  toggleMode: (mode: SpotlightMode) => void;
  setPageSearchResults: (results: PageSearchResult[]) => void;
  addToHistory: (route: string) => void;
  showAnimationButtons: () => void;
  reset: () => void;
}

const loadHistory = (): string[] => {
  try {
    const stored = localStorage.getItem(HISTORY_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

const saveHistory = (routes: string[]) => {
  try {
    localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(routes));
  } catch {
    // Ignore storage errors
  }
};

export const useSpotlightStore = create<SpotlightState>((set, get) => ({
  // Initial state
  isOpen: false,
  query: '',
  selectedIndex: 0,
  mode: 'search',
  showActionsButton: false,
  showHistoryButton: false,
  showPageSearchButton: false,
  pageSearchResults: [],
  historyRoutes: loadHistory(),

  // Actions
  open: () => {
    set({
      isOpen: true,
      query: '',
      selectedIndex: 0,
      mode: 'search',
      pageSearchResults: [],
      showActionsButton: false,
      showHistoryButton: false,
      showPageSearchButton: false,
    });
  },

  close: () => {
    set({
      isOpen: false,
      query: '',
      mode: 'search',
      pageSearchResults: [],
    });
  },

  setQuery: (query: string) => {
    const currentMode = get().mode;
    set({
      query,
      selectedIndex: 0,
      // Reset to search mode when typing (unless in pageSearch mode)
      mode: currentMode === 'pageSearch' ? 'pageSearch' : 'search',
    });
  },

  setSelectedIndex: (index: number) => set({ selectedIndex: index }),

  setMode: (mode: SpotlightMode) => {
    set({
      mode,
      selectedIndex: 0,
      pageSearchResults: mode !== 'pageSearch' ? [] : get().pageSearchResults,
    });
  },

  toggleMode: (mode: SpotlightMode) => {
    const currentMode = get().mode;
    set({
      mode: currentMode === mode ? 'search' : mode,
      selectedIndex: 0,
      pageSearchResults: mode !== 'pageSearch' ? [] : get().pageSearchResults,
    });
  },

  setPageSearchResults: (results: PageSearchResult[]) => set({ pageSearchResults: results }),

  addToHistory: (route: string) => {
    const { historyRoutes } = get();
    // Remove if exists, add to front, limit to max
    const filtered = historyRoutes.filter(r => r !== route);
    const newHistory = [route, ...filtered].slice(0, MAX_HISTORY_ITEMS);
    saveHistory(newHistory);
    set({ historyRoutes: newHistory });
  },

  showAnimationButtons: () => {
    // Stagger the button animations
    setTimeout(() => set({ showActionsButton: true }), 250);
    setTimeout(() => set({ showPageSearchButton: true }), 350);
    setTimeout(() => set({ showHistoryButton: true }), 400);
  },

  reset: () => {
    set({
      isOpen: false,
      query: '',
      selectedIndex: 0,
      mode: 'search',
      showActionsButton: false,
      showHistoryButton: false,
      showPageSearchButton: false,
      pageSearchResults: [],
    });
  },
}));
